import { z } from 'zod/v4';

// The social-network id and icon-style enums live in
// `common/schemas/legacy/social-network` — shared with the form widget's
// chat-bot bag.
import { socialIconTypeSchema, socialNetworkSchema } from '../../../common/schemas/legacy';

/**
 * Rendered appearance of the social entries, mirroring `EButtonType`. The
 * editor only toggles between `'icon'` and `'button'`; `'iconText'` is the
 * legacy third member kept for parity with old persisted data.
 */
export const socialAppearanceSchema = z.enum(['iconText', 'icon', 'button']);

/**
 * One entry of the widget's `socials` array — matches the FormGroup produced
 * by `getSocialItemFormGroup` in the host app's social form factory.
 *
 * `description` and `preventLinkConverting` are `nullish()`: both post-date
 * the widget (the description input never shipped; the Telegram
 * link-conversion opt-out came later), so older persisted items omit them.
 */
export const socialItemSchema = z.object({
    socialNetwork: socialNetworkSchema,
    type: socialAppearanceSchema,
    iconType: socialIconTypeSchema,
    name: z.string(),
    link: z.string(),
    description: z.string().nullish(),
    preventLinkConverting: z.boolean().nullish(),
});

export type SocialItem = z.infer<typeof socialItemSchema>;

/**
 * Default social item. Mirrors `getSocialItemFormGroup()` defaults in the
 * host app — a Telegram icon entry with an empty link.
 */
export const createSocialItem = (overrides: Partial<SocialItem> = {}): SocialItem => ({
    socialNetwork: 'telegram',
    type: 'icon',
    iconType: 'round',
    name: 'Telegram',
    link: '',
    description: '',
    preventLinkConverting: false,
    ...overrides,
});
